import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Skeleton } from "@/components/kit";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import type { CustomerProductAffinityResultDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";

const chartConfig = {
  returningCustomerCount: {
    label: "ລູກຄ້າຊື້ຊ້ຳ",
    color: "var(--chart-2)",
  },
} satisfies ChartConfig;

type CustomerAffinityChartProps = {
  result?: CustomerProductAffinityResultDTO;
  isLoading?: boolean;
  limit?: number;
};

function formatVariantLabel(
  row: CustomerProductAffinityResultDTO["data"][number],
) {
  const name = row.variantName
    ? `${row.productName} · ${row.variantName}`
    : row.productName;
  return name.length > 22 ? `${name.slice(0, 22)}…` : name;
}

export function CustomerAffinityChart({
  result,
  isLoading,
  limit = 12,
}: CustomerAffinityChartProps) {
  if (isLoading) {
    return <Skeleton className="h-[280px] w-full rounded-2xl" />;
  }

  const rows = (result?.data ?? []).slice(0, limit);

  if (rows.length === 0) {
    return (
      <div className="brand-card flex h-[280px] items-center justify-center p-6 text-muted-foreground text-sm">
        ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້
      </div>
    );
  }

  const data = rows.map((row) => ({
    label: formatVariantLabel(row),
    fullLabel: row.variantName
      ? `${row.productName} · ${row.variantName}`
      : row.productName,
    returningCustomerCount: row.returningCustomerCount,
    orderCount: row.orderCount,
    revenue: Number(row.revenue),
  }));

  return (
    <div className="brand-card min-w-0 overflow-hidden p-4 sm:p-5">
      <h2 className="mb-1 font-display text-lg">ສິນຄ້າທີ່ລູກຄ້າຊື້ຊ້ຳ</h2>
      <p className="mb-4 text-muted-foreground text-xs">
        ລູກຄ້າຊື້ຊ້ຳໃນຊ່ວງ — ບໍ່ລວມຂອງແຖມ
      </p>
      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-[260px] w-full min-w-0"
      >
        <BarChart
          data={data}
          layout="vertical"
          margin={{ left: 4, right: 12, top: 8, bottom: 0 }}
        >
          <CartesianGrid horizontal={false} />
          <XAxis type="number" tickLine={false} axisLine={false} hide />
          <YAxis
            type="category"
            dataKey="label"
            width={96}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value, _name, item) => (
                  <span className="font-medium tabular-nums">
                    {value} ຄົນ · {item.payload.orderCount} ອໍເດີ້ ·{" "}
                    {formatLak(String(item.payload.revenue))}
                  </span>
                )}
                labelFormatter={(_label, payload) =>
                  payload?.[0]?.payload?.fullLabel ?? _label
                }
              />
            }
          />
          <Bar
            dataKey="returningCustomerCount"
            fill="var(--color-returningCustomerCount)"
            radius={[0, 4, 4, 0]}
          />
        </BarChart>
      </ChartContainer>
    </div>
  );
}
